<template>
  <!-- Simple variant: 아이콘 + 텍스트만 -->
  <div 
    v-if="variant === 'simple'"
    :class="cn(
      'j-empty-state-simple',
      'flex flex-col items-center justify-center p-8 text-center',
      'rounded-sm border-4 border-dashed',
      props.class
    )"
  >
    <JIcon 
      v-if="icon || $slots.icon" 
      :name="icon || 'inbox'" 
      class="w-12 h-12 text-muted-foreground mb-3"
    >
      <slot name="icon" />
    </JIcon>
    
    <p v-if="title || $slots.title" class="text-sm text-muted-foreground">
      <slot name="title">{{ title }}</slot>
    </p>
  </div>

  <!-- Default variant: 풀 스타일 -->
  <div 
    v-else
    :class="cn(
      'j-empty-state',
      'flex flex-col items-center justify-center',
      'p-12 text-center',
      'max-w-lg mx-auto',
      'rounded-sm border border-border',
      'bg-card shadow-sm',
      props.class
    )"
  >
    <!-- 아이콘 (원형 배경) -->
    <div v-if="icon || $slots.icon" class="j-empty-icon-wrapper">
      <slot name="icon">
        <JIcon 
          v-if="icon" 
          :name="icon" 
          class="w-12 h-12 text-muted-foreground"
        />
      </slot>
    </div>

    <!-- 제목 -->
    <h3
v-if="title || $slots.title" 
        class="text-xl font-semibold text-foreground mb-2 tracking-tight">
      <slot name="title">{{ title }}</slot>
    </h3>

    <!-- 설명 -->
    <p
v-if="description || $slots.description" 
       class="text-sm text-muted-foreground mb-6 max-w-sm leading-relaxed">
      <slot name="description">{{ description }}</slot>
    </p>

    <!-- 액션 버튼 -->
    <slot name="action">
      <JButton 
        v-if="actionText" 
        styletype="primary"
        size="sm"
        @click="emit('action')"
      >
        {{ actionText }}
      </JButton>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { JIcon, JButton } from '@/components/atoms'
import { cn } from '@/lib/utils'
import type { JEmptyStateProps, JEmptyStateEmits } from '@/types/empty-state.types'

const props = withDefaults(defineProps<JEmptyStateProps>(), {
  icon: 'inbox',
  variant: 'default',
})

const emit = defineEmits<JEmptyStateEmits>()
</script>

<style scoped>
.j-empty-state {
  transition: all 0.2s ease;
}

.j-empty-state-simple {
  border-color: hsl(var(--border) / 0.5);
  background: hsl(var(--muted) / 0.1);
  transition: all 0.2s ease;
}

.j-empty-state-simple:hover {
  border-color: hsl(var(--border) / 0.8);
  background: hsl(var(--muted) / 0.15);
}

.j-empty-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 5rem;
  margin-bottom: 1.5rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, 
    hsl(var(--primary) / 0.08) 0%, 
    hsl(var(--primary) / 0.02) 100%);
  border: 1px solid hsl(var(--border));
}

@media (prefers-reduced-motion: no-preference) {
  .j-empty-icon-wrapper {
    animation: pulse 3s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  50% { 
    opacity: 0.85; 
    transform: scale(1.02);
  }
}
</style>
